import { UpperCasePipe } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, Injector, OnInit, signal, Signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { FormControlType, FormField, FormGroupComponent } from '@iot-platform/iot-platform-ui';
import { FormsHelpers } from '@iot-platform/iot-platform-utils';
import { Product } from '@iot-platform/models/oyan';
import { TranslatePipe } from '@ngx-translate/core';
import { get } from 'lodash';
import { map } from 'rxjs';

export interface ProductFormModel {
  name: string;
  shortName: string;
  displayName: string;
}

export interface ProductFormDialogData {
  product: Product;
}

@Component({
  selector: 'oyan-ui-product-edit-form',
  imports: [
    FlexLayoutModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatToolbarModule,
    MatIconModule,
    TranslatePipe,
    FormGroupComponent,
    UpperCasePipe
  ],
  templateUrl: './product-edit-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./product-edit-form.component.scss']
})
export class ProductEditFormComponent implements OnInit {
  readonly matDialogRef: MatDialogRef<ProductEditFormComponent> = inject(MatDialogRef<ProductEditFormComponent>);
  readonly data: ProductFormDialogData = inject(MAT_DIALOG_DATA, { optional: true }) || { product: null };
  fields: WritableSignal<Partial<FormField>[]> = signal([]);
  form = new FormGroup<
    Partial<{
      name: FormControl<string>;
      shortName: FormControl<string>;
      displayName: FormControl<string>;
    }>
  >({});
  initialFormState = signal<ProductFormModel | null>(null);
  formValueChanges: Signal<Partial<ProductFormModel>>;
  canSubmit = computed(() => {
    const initialFormState = this.initialFormState();
    const formValueChanges = this.formValueChanges();
    return this.form?.valid && JSON.stringify(formValueChanges) !== JSON.stringify(initialFormState);
  });
  protected readonly injector = inject(Injector);
  protected readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.formValueChanges = toSignal(
      this.form.valueChanges.pipe(
        map(() => this.form?.getRawValue()),
        takeUntilDestroyed(this.destroyRef)
      )
    );
  }

  ngOnInit(): void {
    this.initializeFields();
    this.initializeFormEffect();
  }

  initializeFields(): void {
    this.fields.set([
      {
        fxFlex: signal('33%'),
        type: FormControlType.TEXT,
        name: signal('name'),
        label: signal('PRODUCTS.TABLE_CONTENT.PRODUCT_NAME'),
        required: signal(true),
        disabled: signal(true)
      },
      {
        fxFlex: signal('33%'),
        type: FormControlType.TEXT,
        name: signal('displayName'),
        label: signal('PRODUCTS.TABLE_CONTENT.CUSTOMER_PRODUCT_NAME'),
        required: signal(true)
      },
      {
        fxFlex: signal('33%'),
        type: FormControlType.TEXT,
        name: signal('shortName'),
        label: signal('PRODUCTS.TABLE_CONTENT.PRODUCT_SHORT_NAME')
      }
    ]);
  }

  initializeFormEffect(): void {
    effect(
      () => {
        // Initialize form with product data
        const product = this.data.product;
        this.form.addControl(
          'name',
          new FormControl({
            value: get(product, 'name', ''),
            disabled: true
          })
        );
        this.form.addControl('displayName', new FormControl(get(product, 'displayName')));
        this.form.addControl('shortName', new FormControl(get(product, 'shortName')));

        // Set initial form state
        this.initialFormState.set(this.form.getRawValue() as ProductFormModel);
      },
      { injector: this.injector }
    );
  }

  close(): void {
    this.matDialogRef.close();
  }

  save(): void {
    if (FormsHelpers.isFormValid(this.form)) {
      const formValue = this.form.getRawValue();
      this.matDialogRef.close({
        ...this.data.product,
        shortName: formValue.shortName ?? undefined,
        displayName: formValue.displayName ?? undefined
      });
    }
  }
}
